import { OPTIONS } from '@app/framework/constants/options-list.constants';
import { RFI_STATUSES } from '@app/store/rfis/rfi.constants';
import { PersistedFile } from '@app/framework/interfaces/file.type';

export type RFITableItem = {
  id: number;
  rfi_id: string;
  subject: string;
  responsibility?: {
    name: string;
    company: string;
  };
  originator?: {
    name: string;
    company: string;
  };
  due: string;
  remaining_percent: number; // percent
  remaining_days: number;
  status: (typeof RFI_STATUSES)[number];
  tags: number;
};

export enum RFI_STATUS {
  OPEN = 'open',
  CLOSED = 'closed',
  VOID = 'void',
  DRAFT = 'draft',
}

export enum RFI_AUTO_STATUS {
  NOT_SUBMITTED = 'not_submitted',
  NOT_ANSWERED = 'not_answered',
  SEEN = 'seen',
  ANSWERED = 'answered',
  APPROVED = 'approved',
}

export enum CREATE_RFI_PAGE {
  FIRST_PAGE = 1,
  SECOND_PAGE = 2,
}

export type CreateUpdateRFIModel = {
  id?: number;
  project_id: number;
  rfi_number: string;
  status: RFI_STATUS;
  due_date: string;
  rfi_manager_id: number;
  rfi_responsible_id: number;
  originator_user_id: number;
  originator_rfi_id: string;
  estimated_cost: number | null;
  estimated_schedule: number | null;
  subject: string;
  question: string;
  drawing_id: string;
  section_reference: string;
  participant_ids: number[]; // array of user IDs
};

export type RFISortingKeys =
  | 'rfi_number'
  | 'subject'
  | 'rfi_manager'
  | 'originator'
  | 'rfi_responsible'
  | 'due_date'
  | 'submitted_at'
  | 'answered_at'
  | 'answer_approved_at'
  | 'days_remaining'
  | 'status';

export type UpdateRFIModel = CreateUpdateRFIModel & {
  answer?: string;
  answer_status?: RFIAnswerStatus;
};

export type RFIAnswerStatus = 'submitted' | 'draft' | 'approved';

export type RFIUserType = {
  id: number;
  name: string; // e.g. 'general_contractor', 'manager'
};

export type RFIUser = {
  id: number;
  name: string;
  type: RFIUserType;
  company_name: string;
};

export type RFIMessagingGroup = {
  id: number;
  name: string;
  status: string | null; // backend returns null or a status string
};

export type RFIListingItem = {
  id: number;
  rfi_number: string;
  status: RFI_STATUS; // matches enum
  due_date: string;
  subject: string;
  answer_status: string | null;
  answered_at: string | null;
  answer_approved_at: string | null;
  responsible_seen_at: string | null;
  submitted_at: string | null;
  creator: RFIUser;
  project: { id: number; title: string };
  rfi_manager: RFIUser;
  rfi_responsible: RFIUser;
  rfi_originator: RFIUser | null;
  days_remaining: number;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
};

export type RFIListingItemExtended = RFIListingItem & {
  remaining_percent: number;
  total_period_days: number;
  // auto_status: RFI_AUTO_STATUS;
};

export type RFI = {
  id: number;
  rfi_number: string;
  status: RFI_STATUS; // matches enum
  due_date: string;
  subject: string;
  question: string;
  manager_answer: string | null;
  manager_answer_status: RFIAnswerStatus | null;
  manager_answered_at: string | null;
  manager_answered_user: RFIUser | null;
  responsible_answer: string | null;
  responsible_answer_status: RFIAnswerStatus | null;
  responsible_answered_at: string | null;
  responsible_answered_user: RFIUser | null;
  responsible_seen_at: string | null;
  submitted_at: string | null;
  days_remaining: number;
  estimated_cost: number;
  estimated_schedule: number;
  drawing_id: string;
  section_reference: string;
  creator: RFIUser;
  project: { id: number; title: string };
  rfi_manager: RFIUser;
  rfi_responsible: RFIUser;
  rfi_originator: RFIUser | null;
  originator_rfi_id: string;
  participants: RFIUser[]; // empty array if none
  question_files: RFIFile[];
  answer_files: RFIFile[];
  messaging_group: RFIMessagingGroup;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
};

export type RFIFile = PersistedFile & {
  belongs_to: 'question' | 'answer';
  uploaded_by: {
    id: number;
    name: string;
    company_name: string;
  };
};

export type RFIFilterParams = {
  perPage?: number;
  page?: number;
  status?: RFI_STATUS[];
  rfi_manager_id?: number[];
  rfi_responsible_id?: number[];
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

export type RFIAnswerOption = {
  value: OPTIONS;
  label: string;
  icon: string;
  disabled?: boolean;
  class: string;
};

export type RFICompanyGroup = {
  company_name: string;
  users: RFIUser[];
};
